import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X, ChevronDown, ShoppingBag } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { useUiStore } from '../../store/uiStore'
import { useBookingStore } from '../../store/bookingStore'
import { useCartStore } from '../../store/cartStore'
import { getSalonById } from '../../config/salons'
import BookNowButton from '../booking/BookNowButton'
import { isFeatureEnabled, getDisabledBehavior } from '../../lib/featureFlags'
import type { FlagKey } from '../../config/flags'
import MegaPanel from './MegaPanel'
import MegaServices from './MegaServices'
import MegaTrichology from './MegaTrichology'
import MegaSalons from './MegaSalons'
import MegaShop from './MegaShop'
import MegaHelin from './MegaHelin'
import { getMobileSubmenu } from './mobileSubmenus'
import LanguageSwitcher from './LanguageSwitcher'

type MegaKey = 'services' | 'trichology' | 'salons' | 'shop' | 'helin'

interface PrimaryItem {
  key: string
  path: string
  flag: FlagKey
  mega?: MegaKey
  /** Open the booking picker instead of navigating to `path`. Booksalon
      hosts the gift-card flow at the same per-salon URL as appointments. */
  bookingTrigger?: boolean
}

// Enterprise nav: 7 primary items.
// Gift Cards navigates to the /gift-cards page (the page's own purchase
// button opens the Booksalon flow). Shop is a normal route. Careers stays
// in footer. `bookingTrigger` is retained on the type for any future item
// that should open the picker directly from the nav.
const PRIMARY: PrimaryItem[] = [
  { key: 'navV3.services', path: '/services', flag: 'SERVICES', mega: 'services' },
  { key: 'nav.trichology', path: '/trichology', flag: 'TRICHOLOGY', mega: 'trichology' },
  { key: 'nav.salons', path: '/branches', flag: 'BRANCHES', mega: 'salons' },
  { key: 'nav.membership', path: '/membership', flag: 'MEMBERSHIP' },
  { key: 'nav.giftCards', path: '/gift-cards', flag: 'GIFT_CARDS' },
  { key: 'nav.shop', path: '/shop', flag: 'SHOP', mega: 'shop' },
  { key: 'nav.helin', path: '/about', flag: 'ABOUT', mega: 'helin' },
]

function visiblePrimary(): PrimaryItem[] {
  return PRIMARY.filter((item) => {
    if (isFeatureEnabled(item.flag)) return true
    return getDisabledBehavior(item.flag) !== 'hide'
  })
}


export default function NavbarV3() {
  const { lang = 'en' } = useParams<{ lang: string }>()
  const { t } = useTranslation()
  const location = useLocation()
  const { isMobileMenuOpen, setMobileMenu, setCartDrawer } = useUiStore()
  const cartCount = useCartStore((s) => s.cartCount())
  const openBooking = useBookingStore((s) => s.open)
  const preferredSalonId = useBookingStore((s) => s.preferredSalonId)
  const [scrolled, setScrolled] = useState(false)
  const [openMega, setOpenMega] = useState<MegaKey | null>(null)
  // Which mega is expanded in the mobile drawer accordion (independent of the
  // desktop hover `openMega`).
  const [openMobileMega, setOpenMobileMega] = useState<MegaKey | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navRef = useRef<HTMLElement>(null)

  const currentPath = location.pathname.replace(`/${lang}`, '')
  const isHome = currentPath === '' || currentPath === '/'
  // Solid mocha chrome shows when: not on home, scrolled past hero, OR a mega
  // panel is open (so the panel always reads against a stable dark header,
  // never against transparent hero imagery).
  const showSolidPanel = !isHome || scrolled || openMega !== null
  const items = visiblePrimary()

  // Collapse the mobile accordion and toggle the drawer together, so the menu
  // always reopens clean (without a setState-in-effect cascade).
  const toggleMobileMenu = () => {
    setOpenMobileMega(null)
    setMobileMenu(!isMobileMenuOpen)
  }

  useEffect(() => {
    if (!isHome) return
    const onScroll = () => {
      const hero = document.querySelector('section') as HTMLElement | null
      const heroHeight = hero?.offsetHeight ?? window.innerHeight
      setScrolled(window.scrollY > heroHeight * 0.4)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [isHome])

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpenMega(null), 120)
  }
  const openPanel = (k: MegaKey) => {
    cancelClose()
    setOpenMega(k)
  }

  const handleBookingTrigger = () => {
    setOpenMega(null)
    if (preferredSalonId) {
      const salon = getSalonById(preferredSalonId)
      if (salon) {
        window.open(salon.bookingUrl, '_blank', 'noopener,noreferrer')
        return
      }
    }
    openBooking()
  }

  const megaTop = 'calc(var(--nav-height) + var(--banner-stack-height, 0px))'

  return (
    <>
      <nav
        ref={navRef}
        className="fixed left-0 right-0 z-50 transition-[top] duration-150 [--nav-height:48px] md:[--nav-height:56px]"
        style={{ top: 'var(--banner-stack-height, 0px)' }}
        onMouseLeave={scheduleClose}
      >
        <AnimatePresence>
          {showSolidPanel && (
            <motion.div
              key="nav-panel-v3"
              initial={isHome ? { y: -80, opacity: 0 } : false}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -80, opacity: 0 }}
              transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="absolute inset-0 bg-mocha border-b border-gold/20"
              aria-hidden="true"
            />
          )}
        </AnimatePresence>

        <div className="relative max-w-7xl mx-auto px-4 pr-8 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12 md:h-14">
            <Link
              to={`/${lang}`}
              className="flex-shrink-0"
              onClick={() => setOpenMega(null)}
            >
              <img
                src="/images/brand/logo.avif"
                alt={t('components.logoAlt')}
                width={208}
                height={205}
                className="h-8 md:h-10 w-auto"
              />
            </Link>

            {/* Desktop primary nav - 7 items (Gift Cards opens booking flow) */}
            <div className="hidden lg:flex items-center gap-1">
              {items.map((item) => {
                const isActive = location.pathname.includes(item.path)
                const isOpen = openMega === item.mega
                const maisonUnderline = (active: boolean) => (
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none absolute left-3 right-3 bottom-1 h-px bg-gold origin-left transition-transform duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}
                  />
                )
                if (item.mega) {
                  // Click navigates to the landing page; hover/focus opens
                  // the mega panel. Pattern mirrors Apple / Stripe - the
                  // dropdown is a discovery aid, not a click target.
                  return (
                    <Link
                      key={item.path}
                      to={`/${lang}${item.path}`}
                      onMouseEnter={() => openPanel(item.mega!)}
                      onFocus={() => openPanel(item.mega!)}
                      onClick={() => setOpenMega(null)}
                      className={`group relative font-body text-sm px-3 py-2 inline-flex items-center gap-1 transition-all duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                        isActive || isOpen
                          ? 'text-gold tracking-[0.02em]'
                          : 'text-ivory/85 hover:text-ivory tracking-[0.05em] hover:tracking-[0.02em]'
                      }`}
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                    >
                      <span className="relative">{t(item.key)}</span>
                      <ChevronDown
                        size={11}
                        aria-hidden="true"
                        className={`relative transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                      {maisonUnderline(isActive || isOpen)}
                    </Link>
                  )
                }
                if (item.bookingTrigger) {
                  return (
                    <button
                      key={item.path}
                      onMouseEnter={scheduleClose}
                      onClick={handleBookingTrigger}
                      className="group relative font-body text-sm px-3 py-2 transition-all duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] text-ivory/85 hover:text-ivory tracking-[0.05em] hover:tracking-[0.02em]"
                    >
                      <span className="relative">{t(item.key)}</span>
                      {maisonUnderline(false)}
                    </button>
                  )
                }
                return (
                  <Link
                    key={item.path}
                    to={`/${lang}${item.path}`}
                    onMouseEnter={scheduleClose}
                    onClick={() => setOpenMega(null)}
                    className={`group relative font-body text-sm px-3 py-2 transition-all duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isActive
                        ? 'text-gold tracking-[0.02em]'
                        : 'text-ivory/85 hover:text-ivory tracking-[0.05em] hover:tracking-[0.02em]'
                    }`}
                  >
                    <span className="relative">{t(item.key)}</span>
                    {maisonUnderline(isActive)}
                  </Link>
                )
              })}
            </div>

            {/* Right cluster - entering it closes any open mega so it
                doesn't sit on top of the language / cart / call buttons.
                min-w-0 keeps the rightmost tap target (hamburger) from being
                shoved past the header's edge; the bar's pr-8 (vs px-4) on
                mobile gives the icon clear breathing room from the viewport
                edge (pr-6 still read tight against the 24px Menu glyph). */}
            <div
              className="flex items-center gap-2 md:gap-3 min-w-0"
            >
              <LanguageSwitcher onOpen={() => setOpenMega(null)} />

              <button
                type="button"
                onClick={() => setCartDrawer(true)}
                aria-label={t('cart.title')}
                className="relative flex items-center justify-center p-1 text-ivory/85 hover:text-ivory transition-colors"
              >
                <ShoppingBag size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-gold text-ivory font-body text-[9px] leading-none">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>

              <BookNowButton size="sm" className="hidden md:inline-flex">
                {t('nav.bookNow')}
              </BookNowButton>

              <button
                onClick={toggleMobileMenu}
                // -my-2 -ml-2 expands the tap target up/down/left without the
                // negative *right* margin that pushed this last item past the
                // header's edge padding and clipped the icon at the viewport.
                className="lg:hidden text-ivory p-2 -my-2 -ml-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label={t('nav.toggleMenu')}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {openMega === 'services' && (
            <MegaPanel isOpen onRequestClose={scheduleClose} topPx={megaTop}>
              <MegaServices lang={lang} onItemClick={() => setOpenMega(null)} />
            </MegaPanel>
          )}
          {openMega === 'trichology' && (
            <MegaPanel isOpen onRequestClose={scheduleClose} topPx={megaTop}>
              <MegaTrichology lang={lang} onItemClick={() => setOpenMega(null)} />
            </MegaPanel>
          )}
          {openMega === 'salons' && (
            <MegaPanel isOpen onRequestClose={scheduleClose} topPx={megaTop}>
              <MegaSalons lang={lang} onItemClick={() => setOpenMega(null)} />
            </MegaPanel>
          )}
          {openMega === 'shop' && (
            <MegaPanel isOpen onRequestClose={scheduleClose} topPx={megaTop}>
              <MegaShop lang={lang} onItemClick={() => setOpenMega(null)} />
            </MegaPanel>
          )}
          {openMega === 'helin' && (
            <MegaPanel isOpen onRequestClose={scheduleClose} topPx={megaTop}>
              <MegaHelin lang={lang} onItemClick={() => setOpenMega(null)} />
            </MegaPanel>
          )}
        </AnimatePresence>
      </nav>

      {/* Mobile menu - 7 primary items */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-40 bg-ivory pt-16 overflow-y-auto"
          >
            <div className="flex flex-col items-stretch gap-1 py-6 px-6">
              {items.map((item) =>
                item.bookingTrigger ? (
                  <button
                    key={item.path}
                    onClick={() => {
                      setMobileMenu(false)
                      handleBookingTrigger()
                    }}
                    className="font-display text-2xl text-mocha hover:text-gold transition-colors min-h-[44px] py-3 w-full text-center"
                  >
                    {t(item.key)}
                  </button>
                ) : item.mega ? (
                  // Mega item: the name navigates to the landing page; the
                  // chevron expands an accordion of the same links the desktop
                  // panel shows (PRJT-119).
                  <div key={item.path} className="w-full">
                    <div className="flex items-center justify-center gap-1">
                      <Link
                        to={`/${lang}${item.path}`}
                        onClick={() => setMobileMenu(false)}
                        className="font-display text-2xl text-mocha hover:text-gold transition-colors min-h-[44px] py-3 text-center"
                      >
                        {t(item.key)}
                      </Link>
                      <button
                        type="button"
                        aria-label={t(item.key)}
                        aria-expanded={openMobileMega === item.mega}
                        onClick={() =>
                          setOpenMobileMega(openMobileMega === item.mega ? null : item.mega!)
                        }
                        className="p-2 -m-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-mocha/60 hover:text-gold transition-colors"
                      >
                        <ChevronDown
                          size={20}
                          className={`transition-transform ${openMobileMega === item.mega ? 'rotate-180' : ''}`}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                    {openMobileMega === item.mega && (
                      <ul className="flex flex-col items-center gap-0.5 pb-3 pt-1">
                        {getMobileSubmenu(item.mega, lang, t).map((sub) => (
                          <li key={sub.to}>
                            <Link
                              to={sub.to}
                              onClick={() => setMobileMenu(false)}
                              className="block font-body text-base text-mocha/70 hover:text-gold transition-colors min-h-[40px] py-2 px-4 text-center"
                            >
                              {sub.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={`/${lang}${item.path}`}
                    onClick={() => setMobileMenu(false)}
                    className="font-display text-2xl text-mocha hover:text-gold transition-colors min-h-[44px] py-3 w-full text-center"
                  >
                    {t(item.key)}
                  </Link>
                ),
              )}
              <BookNowButton
                onClick={() => setMobileMenu(false)}
                size="lg"
                className="mt-6 mx-auto"
              >
                {t('nav.bookNow')}
              </BookNowButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Spacer - only on inner routes */}
      {!isHome && (
        <div
          className="h-12 md:h-14"
          style={{ marginTop: 'var(--banner-stack-height, 0px)' }}
        />
      )}
    </>
  )
}
